<template>
  <pdp-layout>
    <div v-if="isLoyaltyToastBackdropVisible" class="fixed-0 h-full bg-black/75 lg:hidden" />
    <template #accordions>
      <pdp-section-accordions v-if="!isDigitalProduct" />
    </template>

    <template #body>
      <pdp-section-body v-if="!isDigitalProduct" />
    </template>

    <template #breadcrumbs>
      <pdp-section-breadcrumbs />
    </template>

    <template #buy-with-prime>
      <prime-delivery-card v-if="$feature.showBuyWithPrimeOnPDP" />
    </template>

    <template #ctas>
      <pdp-section-ctas v-if="!isDigitalProduct" />
    </template>

    <template #espot-above-accordions>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['above-accordions']"
        :class="brandClasses.eSpotAboveAccordions"
        :page-context="eSpotsWithPageContext?.includes('above-accordions')"
        :section="eSpots?.['above-accordions']"
      />
    </template>

    <template #espot-above-add-to-cart>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['above-add-to-cart']"
        :class="brandClasses.eSpotAboveAddToCart"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('above-add-to-cart')"
        :section="eSpots?.['above-add-to-cart']"
      />
    </template>

    <template #espot-below-accordions>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['below-accordions']"
        :class="brandClasses.eSpotBelowAccordions"
        :page-context="eSpotsWithPageContext?.includes('below-accordions')"
        :section="eSpots?.['below-accordions']"
      />
    </template>

    <template #espot-below-sizes>
      <cms-section
        v-if="$feature.enablePDPESpots && !isDigitalProduct && eSpots?.['below-sizes']"
        :class="brandClasses.eSpotBelowSizes"
        :section="eSpots?.['below-sizes']"
      />
    </template>

    <template #espot-lazy-above-footer>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['lazy-above-footer']"
        :class="brandClasses.eSpotLazyAboveFooter"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('lazy-above-footer')"
        :section="eSpots?.['lazy-above-footer']"
      />
    </template>

    <template #espot-lazy-below-gallery>
      <cms-section
        v-if="!isDigitalProduct && $feature.enablePDPESpots && eSpots?.['lazy-below-gallery']"
        :class="brandClasses.eSpotLazyBelowGallery"
        lazy-media
        :page-context="eSpotsWithPageContext?.includes('lazy-below-gallery')"
        :section="eSpots?.['lazy-below-gallery']"
      />
    </template>

    <template #gallery>
      <pdp-section-gallery />
    </template>

    <template #gift-card>
      <pdp-section-gift-card v-if="isDigitalProduct" />
    </template>

    <template #heading>
      <pdp-section-heading />
    </template>

    <template #klarna-payments>
      <klarna-payments
        v-if="!isDigitalProduct && $feature.showKlarnaOnPDP && product && productPrice"
        :class="brandClasses.klarnaPayments"
        :currency="product.currency"
        :price="productPrice.lowCurrent"
      />
    </template>

    <template #notifications>
      <pdp-section-notifications />
    </template>

    <template #paypal-payments>
      <lazy-paypal-payments
        v-if="!isDigitalProduct && $feature.showPaypalOnPDP && !auth.isEmployee && product && productPrice"
        :amount="productPrice.lowCurrent"
        :class="brandClasses.paypalPayments"
        placement="product"
      />
    </template>

    <template #recommendations>
      <base-lazy-hydrate v-if="$feature.showBloomreachWidget" when="visible">
        <vf-recommendations-widget />
      </base-lazy-hydrate>
    </template>

    <template #reviews>
      <pdp-section-reviews v-if="!isDigitalProduct && $feature.showReviewsOnPDP" />
    </template>

    <template #reviews-summary>
      <pdp-section-reviews-summary v-if="!isDigitalProduct && $feature.showReviewsOnPDP" />
    </template>

    <template #shipping-methods>
      <pdp-section-shipping-methods
        v-if="!isDigitalProduct && $feature.showPickupOptionsOnPDP && isBOPISEnabled"
        ref="shippingMethodsRef"
      />
      <product-shipping-methods-option
        v-else-if="!isDigitalProduct && !$feature.showPickupOptionsOnPDP && isBOPISEnabled && $feature.showEddOnPDP"
        data-test-id="pdp-shipping-methods"
        :description="$t.standardShippingTime"
        icon="shipping"
        :label="$t.shipToHome"
        :selectable="false"
        :show-edd="$feature.showEddOnPDP"
        type="standard"
        :variant="defaultVariant"
      />
    </template>

    <template #shop-the-look>
      <pdp-section-shop-the-look
        v-if="!isDigitalProduct && $feature.pdpShopTheLook && product?.productSets?.length"
      />
    </template>

    <template #size-and-fit>
      <pdp-section-size-and-fit v-if="!isDigitalProduct && showSizeAndFit" />
    </template>

    <template #skeletons>
      <div class="container">
        <vf-breadcrumbs-skeleton
          v-if="!hideBreadcrumbs"
          v-style:display="{
            sm: $feature.breadcrumbVisibilityOnPDP?.sm ? 'flex' : 'none',
            md: $feature.breadcrumbVisibilityOnPDP?.md ? 'flex' : 'none',
            lg: $feature.breadcrumbVisibilityOnPDP?.lg ? 'flex' : 'none',
          }"
          class="mb-4 ml-2 mt-6 lg:mt-7 md:mt-8"
        />
        <pdp-skeleton class="mb-8" />
      </div>
    </template>

    <template #upsells>
      <pdp-section-upsells v-if="$feature.showUpsellsOnPdp" :class="brandClasses.upsellsWrapper" />
    </template>

    <template #variant-selection>
      <pdp-section-variant-selection v-if="!isDigitalProduct" />
    </template>

    <dialog-shop-the-look :class-content="$feature.pdpShopTheLookElevated ? brandClasses.shopTheLookDialogElevate : brandClasses.shopTheLookDialog" />
    <dialog-pickup-in-store size="md" />
    <dialog-size-chart :class-content="brandClasses.sizeChartDialog" :size="$viewport.lg ? 'md' : 'lg'" />
    <dialog-review-write />
    <dialog-try-it-on :class-content="{ 'h-full': useCamera }" />
    <dialog-quickshop class-content="<lg:pb-0" :size="$viewport.lg ? 'lg' : undefined" />
    <dialog-variant-selection />
    <dialog-barcode />
  </pdp-layout>
</template>

<script lang="ts" setup>
import type { Props as VariantSelectionProps } from '#commerce/components/dialog/variant-selection/VariantSelection.vue'
import type { BaseForm as BaseFormType, PdpSectionShippingMethods as PdpSectionShippingMethodsType } from '#components'
import type { ContentProductEspots } from '#root/api/clients/content/data-contracts'
import type {
  DetailsData,
  Product,
  ProductMetaTag,
  ProductReviewsRollup,
  ProductVariants
} from '#root/api/clients/product/data-contracts'
import type { GiftOption } from '#types/cart'
import type { Props as PickupInStoreProps } from '#types/components/product/pickupInStore'
import type { ProductQuestionsParams, ProductReviewsData, ProductReviewsParams } from '#types/components/product/reviews'
import type { InteractionOrigin, ProductContext } from '#types/gtm'
import type { ImageItem, SketchfabItem, VideoItem } from '#types/media'
import type { BaseNotification } from '#types/notification'
import type { AttributeSelection, SelectedVariant } from '#types/product'
import { PdpContextKey } from '#commerce/composables/usePdp/context'

import { ApiErrorCode } from '#root/enums/api'

definePageMeta({
  /**
   * The key is what decides if the page will be re-rendered.
   * Whenever are switching swatches we are storing the original product id on the history state
   * If a route call doesn't contain it, like a recommendation carousel, it re-renders the page
   */
  key: (route) => window?.history?.state?.pageKey || getUrlId(route.path, 'Product'),
  validate: (route) => PRODUCT_URL_REGEX.test(route.path)
})

const { seoData, ...config } = useAppConfig()
const { skipInventoryCheck, skipReviewQuestion } = config.api.brandify
const { defaultImageUrl } = config.components.product
const { alwaysUseGrid } = config.components.vf.mediaGallery
const { showLoyaltyToastOnlyOnPDP } = config.components.vf.loyalty
const {
  allowPreselectShoeSize,
  brandClasses,
  enableColorAsAttribute,
  enableVariantFavorites,
  gallery: galleryConfig,
  eSpotsWithPageContext,
  hideBreadcrumbs,
  questions: questionsConfig,
  quickshop: quickshopConfig,
  reviews: reviewsConfig,
  seoMetaImage,
  shopTheLook: shopTheLookConfig,
  shopTheLookElevate: shopTheLookElevateConfig,
  sizeChart: sizeChartConfig,
  tryItOn: tryItOnConfig,
  variantSelection: variantSelectionConfig,
} = config.pages.pdp
const loyaltyLogo = config.checkout.loyalty.logo
// App configs used inside computed function leak memory
// This clones the config to break the reactivity and fix the leak
// A better solution is probably available by looking deeper into the app configs
const gallery = JSON.parse(JSON.stringify(galleryConfig))

const { brand } = useRuntimeConfig().public
const { selectedStore } = storeToRefs(useBopisStore())
const cart = useCartStore()
const { add, setFavoriteStoreId } = cart
const {
  items: cartItems,
  error: cartError,
  errorMessage,
  errorTitle,
  pending: isCartPending,
  selectedUpsells,
} = storeToRefs(cart)
const favorites = useFavoritesStore()
const isFavoritePending = ref(false)
const { $feature, $gtm, $locale, $sendExtraMonetateEvents, $seo, $t, $viewport, $optimizely } = useNuxtApp()
const { products } = useApi()
const host = useHost()
const route = useRoute()
const router = useRouter()
const toast = useToaster()
const auth = useAuthStore()
const useCamera = ref(false)
const user = useUserStore()
const loyalty = useLoyaltyStore()
const header = useHeaderStore()
const { injectFitFinder } = useFitFinder()
const { getUserEventData } = useAnalytics()
const dialogOption = $feature.pdpShopTheLookElevated
  ? shopTheLookElevateConfig?.dialogOptions ?? shopTheLookConfig?.dialogOptions
  : shopTheLookConfig?.dialogOptions

const { DialogSignIn, DialogPickupInStore, DialogLoyaltySignUp } = useDialogsStore()
const DialogShopTheLook = createDialog('shop-the-look', dialogOption)
const DialogSizeChart = createDialog('size-chart', sizeChartConfig?.dialogOptions)
const DialogReviewWrite = createDialog('review-write', reviewsConfig.dialogOptions.reviewWrite)
const DialogTryItOn = createDialog('try-it-on', tryItOnConfig?.dialogOptions)
const DialogQuickshop = createDialog('quickshop', quickshopConfig?.dialogOptions)
const DialogVariantSelection = createDialog('variant-selection', variantSelectionConfig?.dialogOptions)
const DialogBarcode = createDialog('barcode')

const formRef = ref<InstanceType<typeof BaseFormType> | null>(null)
const shippingMethodsRef = ref<InstanceType<typeof PdpSectionShippingMethodsType> | null>(null)
const error = ref(false)
const addToCartLoading = ref(false)
const checkoutCtaLoading = ref(false)
const seoMetaHead = ref()
const shippingMethod = computed<'standard' | 'pickup' | undefined>(() => shippingMethodsRef.value?.method)
const productId = computed(() => getUrlId(route.path, 'Product'))
const reviewsQuery = reactive<ProductReviewsParams>({
  'paging.from': 0,
  'paging.size': reviewsConfig.pageSize,
  // sort=Language will sort the response so that reviews in their native language are prioritized
  'sort': $feature.pdpReviewTranslate ? 'Language' : 'Newest',
  // get all reviews across locales based on rules in LD -
  // for example, fr-ca will display en-ca + en-us reviews
  'getAll': $feature.pdpReviewTranslate
})
const questionsQuery = reactive<ProductQuestionsParams>({
  pagingFrom: 0,
  pagingSize: questionsConfig.pageSize,
  sort: 'Newest'
})
const selectedVariantWithUpsell = ref<string[]>([])
const dialogNotification = reactive<BaseNotification>({
  message: '',
  type: 'success'
})

const { getSections } = useCms()

const sections = ref<ReturnType<typeof getSections.product>>()
const eSpots = computed<ContentProductEspots['sectionsMap'] | undefined>(() => sections.value?.data.value?.content.sectionsMap)
if ($feature.enablePDPESpots)
  sections.value = getSections.product(productId)

const loadedProduct = useNuxtData(`productDetails-${productId.value}`)
const attributeSelection = reactive<AttributeSelection>({})
const interactionOrigin = getInteractionOrigin()

const {
  productPending,
  product,
  productError,
  inventoryPending,
  inventoryError,
  inventory
} = await useProduct(productId, {
  enableColorAsAttribute,
  onResponse() {
    if (loadedProduct.data?.value)
      loadedProduct.data.value.gallery = []
  },
})
const { defineUser, trackEvent } = useOptimizely()
const { getAttrOptionAvailable, getAttrOptionNotifyMe } = getProductAvailability(attributeSelection, product)

/**
 * is used when open product in new tab from plp/srp
 */
const getStoredHistoryState = (key: string) => {
  const state = localStorage.getItem(key)
  localStorage.removeItem(key)
  return state ? JSON.parse(state) : {}
}

const getHistoryState = (id: string) => history.length === 1 ? getStoredHistoryState(`from_plp_${id}`) : history.state

const pushProductDetailViewEvent = (product) => {
  const state = getHistoryState(product.id)
  $gtm.push('product.onProductDetailView', product, {
    viewType: 'PDP',
    breadcrumbs: deserializeAnalyticsBreadcrumbs(state.breadcrumbs),
    category: state.category,
    searchTerm: state.searchTerm ?? state.suggestedPhrase,
    stockStatus: inventory.value?.variants[productId.value]?.stockStatus
  })
}

// Map breadcrumbs - to review if we can match component and api types for breadcrumbs.
const breadcrumbs = computed(() => product.value?.breadcrumbs || [])
const isNonLoyaltyMember = computed(() => !!(auth.loggedIn && !user.isLoyaltyMember))
const isSignInToBuy = computed(() =>
  !!product.value?.presale
  && (!auth.loggedIn || isNonLoyaltyMember.value))
const isComingSoon = computed(() => $feature.showComingSoonOnPDP && getIsComingSoonProduct(product.value))
const isDigitalProduct = computed(() => product.value?.productType?.toLowerCase() === 'digital')
const isRobisOnly = computed(() => $feature.allowProductReservationOnPDP && product.value?.isRobisOnly)

const setCategoryMetatags = () => {
  // Safely extract category path
  const breadcrumb = breadcrumbs.value.at(-1)
  const categoryURL = window?.history?.state?.categoryURL || breadcrumb?.url || ''
  const categoryMatch = categoryURL.match(CATEGORY_URL_REGEX)

  // Extract category levels
  const categoryPath = categoryMatch?.[0] || categoryURL
  const categoryLevels = categoryPath.split('c/').filter(Boolean)

  // Build meta categories object
  const lastLevel = categoryLevels.at(-1) || ''
  const metaCategories = lastLevel.split('/').filter(Boolean).reduce((acc, category, i) => {
    acc[`product_category_lv${i + 1}`] = category
    return acc
  }, {})

  seoMetaHead.value = useSeoMeta(metaCategories)
}

// avoid calling on CSR as it's invoked in onMounted for that scenario
if (isServer()) setCategoryMetatags()

const pageMetaTags = computed(() => product.value?.pageMetaTags?.reduce<Record<string, ProductMetaTag>>((acc, val) => ({
  ...acc,
  [val.id]: val
}), {}) || {})

const seoPlaceholderValues = () => ({
  brand: $t.brand,
  brandName: $t.brandName,
  productName: product.value?.name || ''
})

const fallbackTitle = computed(() => product.value && replaceAll($seo.pdp.title, seoPlaceholderValues()))
const fallbackDescription = computed(() => product.value && replaceAll($seo.pdp.description, seoPlaceholderValues()))

useHead(() => {
  const { gallery: produtGallery, url } = product.value || {}
  const [canonicalUrl] = `https://${host}${product.value?.url}`.split('?')

  return {
    titleTemplate: pageMetaTags.value.title?.content || fallbackTitle.value,
    link: [{ rel: 'canonical', href: canonicalUrl }],
    script: [
      $feature.configExternalApps.productCompareUrl
        ? {
            src: $feature.configExternalApps.productCompareUrl,
            async: true
          }
        : {}
    ],
    meta: [
      {
        name: 'description',
        content: pageMetaTags.value.description?.content || fallbackDescription.value
      },
      {
        name: 'og:title',
        content: pageMetaTags.value['og:title']?.content || titleTemplate($t, fallbackTitle.value)
      },
      {
        name: 'og:description',
        content: pageMetaTags.value['og:description']?.content || fallbackDescription.value
      },
      {
        name: 'og:image',
        content: produtGallery?.[0].src || seoMetaImage
      },
      {
        name: 'og:type',
        content: 'website'
      },
      {
        name: 'og:url',
        content: `https://${host}${url}`
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image'
      },
      {
        name: 'twitter:creator',
        content: `@${brand}`
      },
      {
        name: 'twitter:site',
        content: `@${brand}`
      },
      ...[
        pageMetaTags.value.robots
          ? {
              name: 'robots',
              content: pageMetaTags.value.robots.content
            }
          : {}
      ],
    ]
  }
})

if (productError.value)
  throw createError({ statusCode: productError.value.statusCode! >= 500 ? 500 : 404 })

let reviewsData = ref<ProductReviewsData>()
let reviewsError = ref()
let reviewsPending = ref(false)

if ($feature.showReviewsOnPDP) {
  // The best approach here would be to use a non-immediate API request and execute it if the flag is enabled,
  // similar to the way Q&A is handled below. Unfortunately, it turns out that in some cases, SSR requests
  // get resolved before all updates associated with non-immediate API calls are run, causing the
  // JSON LD data for reviews to be missing on an initial page load, so we have to fall back to using an immediate call.
  ({ pending: reviewsPending, data: reviewsData, error: reviewsError } = products.reviews(productId, reviewsQuery, {
    lazy: true,
    transform: (data): ProductReviewsData => ({
      paging: data.paging,
      reviews: data.results[0].reviews,
      rollup: reviewsQuery['paging.from'] === 0 ? data.results[0].rollup : reviewsData.value?.rollup,
      pageId: data.results[0].productId,
      reviewsPageId: data.reviewsPageId
    })
  }))

  useSchemaOrg(
    () => !reviewsPending.value
      ? getJsonLdForProductReviews(reviewsData.value as ProductReviewsData, host, `https://${host}${product.value?.url}`, brand)
      : []
  )
}

const maxReviews = computed(() => Math.min(reviewsData.value?.reviews?.length ?? 0, reviewsConfig.pageSize))

useSchemaOrg(() =>
  product.value
    ? [
        getJsonLdForProduct(product.value, seoData, host, $locale, maxReviews.value),
        getJsonLdForBreadcrumbs(product.value, breadcrumbs.value, host)
      ]
    : []
)

const {
  status: questionsStatus,
  data: questionsData,
  error: questionsError,
  execute: questionsExecute
} = products.questions(productId, questionsQuery, { lazy: true, immediate: false, ...skipReviewQuestion?.includes($locale) ? { watch: false } : '' })

const questionsPending = computed(() => questionsStatus.value === 'pending')

const eyebrowStyle = computed(() => {
  const colorHexCode = product?.value?.eyebrow?.colorHexCode
  return colorHexCode && colorHexCode !== 'None' ? { style: `color: #${colorHexCode}` } : {}
})

if ($feature.showReviewQAndAOnPDP) questionsExecute()

if (auth.loggedIn && allowPreselectShoeSize && !user.profile) user.get()

const pending = computed(() => productPending.value || user.pending)

const isRequiredAttributeSelection = computed(
  () => Object.keys(attributeSelection).length >= (enableColorAsAttribute ? 2 : 1)
)
const isVariantPicked = computed(() => isRequiredAttributeSelection.value && product.value?.attributes.every(({ type }) => attributeSelection[type] || type === 'color'))
const selectedVariant = computed(() => {
  const variant = product.value?.variants.find((item) =>
    Object.entries(attributeSelection).every(([key, value]) => !item.attributes[key] || item.attributes[key] === value || key === 'color')
  )
  return (variant && isVariantPicked.value ? variant : null) as SelectedVariant
})
const defaultVariant = computed(() => selectedVariant.value || product.value?.variants[0])

const productPrice = computed(() => {
  if (selectedVariant.value?.maxDigitalProductAmount) {
    return {
      highCurrent: selectedVariant.value.maxDigitalProductAmount,
      lowCurrent: selectedVariant.value.minDigitalProductAmount || 0,
      highOriginal: selectedVariant.value.maxDigitalProductAmount,
      lowOriginal: selectedVariant.value.minDigitalProductAmount || 0,
    }
  }
  else if (selectedVariant.value?.price) {
    return {
      highCurrent: selectedVariant.value.price.current ?? selectedVariant.value.price.original,
      lowCurrent: selectedVariant.value.price.current ?? selectedVariant.value.price.original,
      highOriginal: selectedVariant.value.price.original,
      lowOriginal: selectedVariant.value.price.original,
      historicBestPrice: selectedVariant.value.price.historicBestPrice,
      percentagePriceDiscount: selectedVariant.value.price.percentagePriceDiscount,
      previousBestSalesPrice: selectedVariant.value.price.previousBestSalesPrice,
      salesPriceActiveDate: selectedVariant.value.price.salesPriceActiveDate
    }
  }
  return product.value?.price
})

// OOS means we have a variant that has inventory state !== 'InStock'
const isSelectedProductOutOfStock = computed(() =>
  isVariantPicked.value && selectedVariant.value?.productInventoryState !== 'InStock'
)

const isOutOfStock = computed(
  () => !product.value || isSelectedProductOutOfStock.value || product.value?.allVariantsOutOfStock
)

const isSomeVariantNotifyMe = computed(() => product.value?.variants.some(({ notifyMe }) => !!notifyMe))

const isNotifyMe = computed(() =>
  $feature.showNotifyMeOnPDP
  && !inventoryPending.value
  && (selectedVariant.value?.notifyMe || (isSomeVariantNotifyMe.value && !pending.value && !isVariantPicked.value))
)

const isBOPISEnabled = computed(() => (
  !(isSignInToBuy.value || isComingSoon.value || (isNotifyMe.value && isOutOfStock.value)
    || product.value?.allVariantsOutOfStock || isCustomsProduct(product))
))

// If an item type is image and has a branded preset, construct image properties
const galleryItems = computed<(ImageItem | VideoItem | SketchfabItem)[]>(() => {
  const { gallery: productGallery = [], name } = product.value || {} as Partial<DetailsData>

  return productGallery.map((item) =>
    (item.type === 'image' && gallery?.presets)
      ? {
          ...item,
          src: getImageTransformations(item.src, gallery.presets[
            alwaysUseGrid || productGallery.length > 1
              ? 'src'
              : 'srcZoom']),
          srcLarge: getImageTransformations(item.src, gallery.presets.srcLarge),
          srcZoom: getImageTransformations(item.src, gallery.presets.srcZoom),
          alt: item.alt || name
        }
      : item.type === 'video'
        ? {
            ...item,
            src: getVideoTransformations(item.src),
            width: gallery.presets.src.width,
            height: gallery.presets.src.height,
            poster: getImageTransformations(item.meta.poster, {
              crop: 'fill',
              width: gallery.presets.src.width,
              height: gallery.presets.src.height
            }),
            autoplay: item.meta?.autoplay,
            loop: item.meta?.autoplay
          }
        : item
  )
})

const fetchCustomsData = async (customsRecipeID, size, locale) => {
  try {
    const [{ sku }, imageUrl] = await Promise.all([
      $fetch(`/fapi/customs-sku/${customsRecipeID}`, { query: { locale, size } }),
      $fetch(`/fapi/customs-image/${customsRecipeID}`, {
        query: { locale },
        default: () => defaultImageUrl
      })
    ])
    return { productId: sku, customsProductImageURL: imageUrl }
  }
  catch {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong,
        message: $t.itemCannotAddedToCart,
      }
    })
    return { productId: undefined, customsProductImageURL: undefined }
  }
}

// Add to cart
const addToCart = async (
  {
    suppressMiniCart,
    extendedGtmConfig,
    handleValidation = true,
    giftOption,
    customAmount
  }: {
    suppressMiniCart?: boolean
    extendedGtmConfig?: ProductContext & { appSource?: InteractionOrigin }
    handleValidation?: boolean
    giftOption?: GiftOption
    customAmount?: { amount?: number }
  } = {}
): Promise<boolean> => {
  if (isSignInToBuy.value) {
    if (isNonLoyaltyMember.value)
      await DialogLoyaltySignUp.open({ formLocation: 'modal:single:myaccount:none', isSignInToBuy: true })
    else
      await DialogSignIn.open({ formLocation: 'modal:single:pdp:none', isSignInToBuy: true })

    if (auth.loggedIn && !isNonLoyaltyMember.value) {
      await until(isCartPending).toBe(false)
      addToCart()
    }
    return true
  }

  if (handleValidation) {
    error.value = !formRef.value?.validate(true)
  }

  if (error.value) return error.value

  if (!selectedVariant.value) {
    toast.add({
      props: {
        type: 'error',
        message: $t.apiMessages.GEN500
      }
    })
  }

  addToCartLoading.value = true
  const upc = selectedVariant.value?.upc
  const storeId = shippingMethod.value === 'pickup' ? selectedStore.value?.storeid : undefined

  const { productId, customsProductImageURL } = product.value?.customsRecipeID && product.value?.dummyCustoms
    ? await fetchCustomsData(product.value.customsRecipeID, selectedVariant.value?.attributes.size, $locale)
    : { productId: selectedVariant.value?.id, customsProductImageURL: undefined }

  if (!productId) {
    addToCartLoading.value = false
    return false
  }

  if (selectedUpsells.value.length >= 1)
    selectedVariantWithUpsell.value.push(productId, ...selectedUpsells.value)

  const payload = selectedVariantWithUpsell.value.length >= 1
    ? selectedVariantWithUpsell.value
    : productId

  if (productId) {
    const state = getHistoryState(product.value!.id)

    setGtmCartProductsMap(productId, product.value)
    if (state?.category) setGtmCartProductVariant(productId, { category: state.category })
    if (extendedGtmConfig?.appSource) setGtmCartProductVariant(productId, extendedGtmConfig)
  }

  if (storeId) setFavoriteStoreId(storeId)

  try {
    await withRetryAfterTime(() => add({
      product: payload || '',
      upc,
      suppressMiniCart: typeof suppressMiniCart === 'boolean' ? suppressMiniCart : undefined,
      giftOption,
      customAmount,
      maxQty: inventory.value?.variants[productId]?.quantity ?? 9999999,
      extendedGtmConfig,
      storeId,
      recipeId: isCustomsProduct(product) ? product.value?.customsRecipeID : undefined,
      precreatedCustomsCode: product.value?.dummyCustoms ? product.value.id : undefined,
      productImageURL: customsProductImageURL,
      dummyCustomsPdpUrl: product.value?.dummyCustoms ? window.location.href : undefined
    }), 2000, [ApiErrorCode.GWP_PRODUCT])
  }
  catch (err) {
    log.error(err)
  }

  if (cartError.value) {
    toast.add({
      props: {
        type: 'error',
        title: errorTitle.value,
        message: errorMessage.value
      }
    })
    if (productId && product.value) removeGtmCartProductsMap(productId, product.value?.id)
  }

  selectedVariantWithUpsell.value = []
  addToCartLoading.value = false

  return cartError.value
}

const addToCartAndCheckout = async () => {
  checkoutCtaLoading.value = true
  const error = await addToCart({ suppressMiniCart: true })
  checkoutCtaLoading.value = false
  if (!error) router.push(useLocalisedRoute({ path: '/checkout' }))
}

const setNotification = ({ message, type }: BaseNotification) => {
  dialogNotification.message = message
  dialogNotification.type = type

  if (message) setTimeout(() => scrollToElement('pdpContainer'), 250)
}
const resetNotification = () => setNotification({ message: '', type: 'success' })

const findInStore = async (
  attributeSelectionLabels: Record<string, { label: string, value: string }>,
  mode: PickupInStoreProps['mode'] = 'find'
) => {
  error.value = !formRef.value?.validate(true)

  if (error.value) return

  $gtm.push('pdpPage.onFindInStore')

  const productId = selectedVariant.value?.id

  if (productId) {
    await DialogPickupInStore.open({
      item: {
        ...product.value!,
        productId
      },
      selectedAttributes: attributeSelectionLabels,
      mode,
      skipInventoryCheck: skipInventoryCheck?.includes($locale)
    })

    closeDialogs()
  }
}

// Favorites
const isFavorite = computed(() => favorites.isFavorite(
  (enableVariantFavorites
    ? selectedVariant.value?.id
    : product.value?.id) || ''
))

const productColor = useProductColor(product)

const attributeSelectionLabels = computed(() =>
  product.value?.attributes.reduce((labels: Record<string, string>, { options, type }) => ({
    ...labels,
    [type]: type === 'color'
      ? productColor.value?.label
      : options.find((option) => option.value === attributeSelection[type])?.label
  }), {}) as ProductVariants[number]['attributes']
)

const nextAttribute = computed(() => {
  const index = Object.entries(attributeSelectionLabels.value).findIndex(
    ([type, value]) => (enableColorAsAttribute || type !== 'color') && !value
  )

  return product.value?.attributes[index]
})

const triggerApplePaySession = () => {
  // check for error in the product selection
  error.value = !formRef.value?.validate(true)

  if (!error.value) {
    const getPayload = (selectedVariantSku, selectedUpsellsValue) => {
      if (selectedUpsellsValue.length >= 1)
        return [...selectedVariantWithUpsell.value, selectedVariantSku]
      else
        return selectedVariantSku
    }

    const selectedVariantSku = selectedVariant.value?.id || ''
    const selectedUpsellsValue = selectedUpsells.value
    const productId = getPayload(selectedVariantSku, selectedUpsellsValue)
    const { storeId, shippingContact } = (shippingMethod.value === 'pickup' && selectedStore.value?.storeid)
      ? {
          storeId: selectedStore.value?.storeid,
          shippingContact: {
            addressLines: [
              selectedStore.value?.address1,
              selectedStore.value?.address2
            ].filter((value): value is string => !!value),
            locality: selectedStore.value?.city || '',
            administrativeArea: selectedStore.value?.province || selectedStore.value?.state || selectedStore.value?.country || '',
            countryCode: selectedStore.value?.country || '',
            postalCode: selectedStore.value?.postalcode || ''
          }
        }
      : { storeId: undefined, shippingContact: undefined }
    const payload = { productId, upc: selectedVariant.value?.upc, storeId, shippingContact }

    $gtm.push('checkout.onLoadEventData', 'APPLE_PAY')

    // initiate the temporary applePay only after clicking the button
    const { initApplePaySession } = useApplePay('applePayPdp')
    initApplePaySession(payload)

    if (cartError.value) {
      toast.add({
        props: {
          type: 'error',
          title: errorTitle.value,
          message: errorMessage.value
        }
      })
    }
  }
}

const addFavorite = async (id?: string, productColor?: string) => {
  error.value = !!(enableVariantFavorites && !formRef.value?.validate(true))
  if (error.value || !id) return

  const { error: addFavoriteError } = await favorites.add(id)
  if (addFavoriteError) {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong
      }
    })
  }
  else {
    toast.add({
      props: {
        type: 'success',
        title: $t.favoritesNotification.added,
        ctas: [{
          label: $t.favoritesNotification.buttonLabel,
          to: '/favorites'
        }]
      }
    })
    $gtm.push('pdpPage.onAddToFavorites', `${productId.value} - ${productColor}`)
  }
}

const removeFavorite = async (id?: string, productColor?: string) => {
  error.value = !!(enableVariantFavorites && !formRef.value?.validate(true))
  if (error.value || !id) return

  const { error: removeFavoriteError } = await favorites.remove(id)

  if (removeFavoriteError) {
    toast.add({
      props: {
        type: 'error',
        title: $t.somethingWentWrong
      }
    })
  }
  else {
    toast.remove('favorite-removed')
    toast.add({
      id: 'favorite-removed',
      props: {
        type: 'success',
        title: $t.favoritesNotification.removed,
        ctas: [{
          label: $t.favoritesNotification.buttonLabel,
          to: '/favorites'
        }]
      }
    })
    $gtm.push('pdpPage.onRemoveFromFavorites', `${productId.value} - ${productColor}`)
  }
}

const toggleFavorites = async (productColor?: string) => {
  const id = (enableVariantFavorites ? selectedVariant.value?.id : product.value?.id)
  isFavoritePending.value = true
  try {
    if (isFavorite.value)
      await removeFavorite(id, productColor)
    else
      await addFavorite(id, productColor)
  }
  finally {
    isFavoritePending.value = false
  }
}

const isValidAttributeValue = (selection: string, options: any) => options.some(
  ({ productUrl, value }) => value === selection && (!productUrl || route.path === productUrl)
)

const selectColor = async (_id, url, colorLabel?: string) => {
  const _url = new URL(url, document.baseURI)
  const color = _url.searchParams.get('color') || ''
  const id = url ? getUrlId(url, 'Product') : color
  const currentQuery = Object.fromEntries((new URLSearchParams(window.location.search)).entries())

  if (colorLabel)
    $gtm.push('pdpPage.onSelectColor', color ? `${id}${color} - ${colorLabel}` : `${id} - ${colorLabel}`)

  if (color) attributeSelection.color = color

  router.replace(
    color
      ? {
          query: { ...currentQuery, color },
          state: { colorChange: true, pageKey: window.history.state.pageKey || productId.value }
        }
      : {
          path: url,
          query: currentQuery,
          state: { colorChange: true, pageKey: window.history.state.pageKey || productId.value }
        }
  )

  // Reset the error state when navigating between colours
  // to prevent the error message from showing up when we remove an attribute selection
  // due to the selected value not being present on the colourway we're navigating to
  error.value = false
}

const selectSize = (type, value, label?: string) => {
  attributeSelection[type] = value
  const currentQuery = Object.fromEntries((new URLSearchParams(window.location.search)).entries())
  router.replace({
    query: { ...currentQuery, ...attributeSelection },
    state: { pageKey: window.history.state.pageKey || productId.value }
  })

  if (label)
    $gtm.push('pdpPage.onSelectSize', `${product.value?.id} - ${label}`)
}

const preselectAttributes = async () => {
  if (!inventory.value)
    await until(inventoryPending).toBe(false)
  // "Invalid" in this context means no longer present after switching colours
  const invalidQueryTypes: string[] = []

  product.value?.attributes.forEach(({ type, options }) => {
    if (!enableColorAsAttribute && type === 'color') return

    const availableOptions = options.filter((x) => x.available)
    const queryValue = (route.query?.[type] || '') as string
    const validQueryValue = queryValue && isValidAttributeValue(queryValue, options)

    // Remove invalid selection values from the component data
    if (attributeSelection[type] && !isValidAttributeValue(attributeSelection[type]!, options))
      delete attributeSelection[type]

    if (validQueryValue)
      attributeSelection[type] = queryValue
    else if (options?.length === 1)
      attributeSelection[type] = options[0].value
    else if (availableOptions.length === 1)
      attributeSelection[type] = availableOptions[0].value
    else if ($feature.enablePreselectFirstWidth && type === 'width')
      attributeSelection[type] ??= availableOptions[0]?.value || options[0].value
    else if (allowPreselectShoeSize && type === 'size' && product.value?.productType === 'Footwear')
      attributeSelection[type] = user.preferredShoeSize
    else if (type === 'color')
      attributeSelection[type] = (options?.find(({ defaultColor }) => defaultColor) || options[0])?.value
    else if (type === 'fitType') attributeSelection[type] = options?.[0]?.value || options?.[0]?.label

    if (queryValue && !validQueryValue)
      invalidQueryTypes.push(type)
  })

  // Remove invalid selection values from the query string
  // Adds default selection to the query string
  const queryParams = { ...attributeSelection, ...route.query }
  router.replace({
    ...route,
    query: {
      ...Object.fromEntries(Object.entries(queryParams).filter(([key]) => !invalidQueryTypes.includes(key)))
    },
    state: { pageKey: window.history.state.pageKey || productId.value }
  })

  formRef.value?.validate()
}

const isLoyaltyToastBackdropVisible = ref(false)

const displayLoyaltyToast = () => {
  if (
    !$feature.showLoyaltyRewardsInUtilityNav
    || !showLoyaltyToastOnlyOnPDP
    || !user.details
    || !$viewport.sm
  ) {
    return
  }

  const isLoyaltyToastAlreadyDisplayed = useSessionStorage('isLoyaltyToastAlreadyDisplayed', false)
  if (isLoyaltyToastAlreadyDisplayed.value) return

  const closeLoyaltyToastDelay = 5000

  const greetingText = user.details?.firstName
    ? replaceAll($t.loyaltyRewardsDetails.greetingWithName, { name: user.details.firstName })
    : $t.loyaltyRewardsDetails.greeting
  const rewardAmountText = replaceAll($t.loyaltyRewardsDetails.pointsAmount, { amount: loyalty.currentReward })

  const bottomText = loyalty.vouchers?.expiringLoyaltyPoints !== undefined
    ? replaceAll($t.rewardPointsExpireInNextDays, {
        points: loyalty.vouchers.expiringLoyaltyPoints,
        days: loyalty.vouchers.expiringLoyaltyPointsDays,
      })
    : ''
  toast.add({
    props: {
      type: '',
      classMessage: 'ws-pre-wrap',
      message: `${greetingText}\n${rewardAmountText}`,
      bottomText,
      icon: loyaltyLogo ? { src: loyaltyLogo, size: '3xl' } : undefined,
    },
    delay: closeLoyaltyToastDelay,
  })
  isLoyaltyToastAlreadyDisplayed.value = true
  isLoyaltyToastBackdropVisible.value = true
  setTimeout(() => isLoyaltyToastBackdropVisible.value = false, closeLoyaltyToastDelay)
}

const onZoom = (index: number, zoomFactor: number) => {
  if (zoomFactor) $gtm.push('pdpPage.onClickFullImage', `${product.value?.name} | ${index + 1}`)
  $gtm.push(
    'pdpPage.onImageZoom',
    zoomFactor ? `${zoomFactor * 100}% | ${index + 1}` : `Reset Zoom | ${index + 1}`
  )
}

const openQuickshop = (product: Product, gtmConfig?: ProductContext & { appSource?: InteractionOrigin }) =>
  DialogQuickshop.open({ product, gtmConfig })

const openShopTheLook = (productSet) => {
  $gtm.push('shopTheLook.onClickOpenModal', productSet.productSetId, { bundleId: productSet.productSetId })
  DialogShopTheLook.open({ productSet }).then((automatic) => {
    if (!automatic)
      $gtm.push('shopTheLook.onClickCloseModal', productSet.productSetId, { bundleId: productSet.productSetId })
  })
}

// Size & Fit
const fitRating = computed(() =>
  reviewsData.value?.rollup?.properties?.filter(({ display_type }) => display_type === 'histogram').at(-1)
)

const showSizeAndFit = computed(() => {
  const { fitSize, modelMeasurements = [], sizeChart } = product.value || {} as Partial<DetailsData>
  const { reviewCount } = reviewsData.value?.rollup || {} as Partial<ProductReviewsRollup>

  return $feature.showSizeAndFitOnPDP
    && Boolean(modelMeasurements.length || ($feature.showFitRatingOnPDP && reviewCount && fitRating.value)
      || fitSize || sizeChart)
})

const openSizeGuide = (options?: { showFitRating?: boolean, keep?: boolean }) => {
  $gtm.push('pdpPage.onOpenSizeGuide')
  if (product.value?.sizeChart) {
    DialogSizeChart.keep(!!options?.keep).open({
      productType: `${product.value.sizeChart}`,
      showFitRating: options?.showFitRating,
      fitRating: fitRating.value,
      reviewsData: reviewsData.value
    })
  }
}

const tryItOn = () => {
  $gtm.push('pdpPage.onTryItOn')
  DialogTryItOn.open({ productId: productId.value })
}

const handleNextAttribute = (options: Omit<VariantSelectionProps, 'attribute'> = {}, callback?: () => void) => {
  if (!nextAttribute.value) return callback?.()

  DialogVariantSelection.open({ attribute: nextAttribute.value, ...options }).then((payload) => {
    if (payload?.handleNextAttribute) handleNextAttribute(options, callback)
  })
}

provide(PdpContextKey, {
  addToCart,
  addToCartAndCheckout,
  addToCartLoading,
  attributeSelection,
  attributeSelectionFormRef: formRef,
  attributeSelectionLabels,
  checkoutCtaLoading,
  defaultVariant,
  dialogNotification,
  eSpots,
  error,
  eyebrowStyle,
  findInStore,
  fitRating,
  galleryItems,
  getAttrOptionAvailable,
  getAttrOptionNotifyMe,
  handleNextAttribute,
  inventory,
  inventoryError,
  isComingSoon,
  isDigitalProduct,
  isFavorite,
  isFavoritePending,
  isNonLoyaltyMember,
  isNotifyMe,
  isOutOfStock,
  isProductOrInventoryPending: computed(() => inventoryPending.value || productPending.value),
  isRobisOnly,
  isSelectedProductOutOfStock,
  isSignInToBuy,
  isSomeVariantNotifyMe,
  nextAttribute,
  noVariantSelected: () => error.value = true,
  onZoom,
  openQuickshop,
  openShopTheLook,
  openSizeGuide,
  pending,
  product,
  productColor,
  productError,
  productId,
  productPending,
  productPrice,
  questionsData,
  questionsError,
  questionsPending,
  questionsQuery,
  resetNotification,
  reviewsData,
  reviewsError,
  reviewsPending,
  reviewsQuery,
  selectColor,
  selectSize,
  selectedVariant,
  setNotification,
  shippingMethod,
  showSizeAndFit,
  toggleFavorites,
  triggerApplePaySession,
  tryItOn
})

watch(pending, () => {
  if (isServer() || pending.value) return

  history.state.colorChange && preselectAttributes()
})

watch([() => auth.loggedIn, () => user.pending, () => loyalty.vouchersPending], () => {
  if (auth.loggedIn && !user.pending && !loyalty.vouchersPending)
    displayLoyaltyToast()
}, { immediate: true })

onMounted(async () => {
  // if condition can be removed in GLOBAL15-68785 and until condition will be updated
  if (!product.value)
    await until(productPending).toBe(false)

  setInteractionOrigin(interactionOrigin)
  setCategoryMetatags()

  if (!history.state.colorChange) {
    $sendExtraMonetateEvents(product.value, 'monetate:context:ProductDetailView')
    trackEvent('product_view', {
      product: product.value
    })
    $gtm.push('page.onLoadPageData', route, {
      breadcrumbs: breadcrumbs.value,
      categoryId: product.value?.primaryCategoryId?.toString(),
      pageNameEng: pageMetaTags.value?.defaultPageName?.content,
      pageTitle: await getPageTitle(),
      searchType: history.state.searchType
    })
    const userEventData = await getUserEventData()
    $gtm.push('user.onLoadUserData', userEventData)
    $gtm.push('page.onRouterChange', route)
    $gtm.push('cart.onCartLoad', cart)
    defineUser()
    $optimizely.dataReady(userEventData)
    trackEvent('page_view', { URL: route.fullPath })
  }

  product.value?.upsell?.length && $gtm.push('upsell.onUpsellImpression', product.value.upsell)

  if ($feature.enablePreloadColorsOnPDP && !history.state.colorChange) {
    const requestIdleCallback = window.requestIdleCallback || setTimeout

    requestIdleCallback(async () => {
      const colorAttr = product.value?.attributes.find(({ type }) => type === 'color')
      const otherColorOptions = colorAttr?.options.filter(({ id }) => id !== productId.value)
      const otherColorIds = otherColorOptions?.map(({ id }) => id).filter(Boolean)

      // This is done, so the responses get stored in the cache storage
      // (check the runtimeCache config in domains/core/config/nuxt/pwa.ts),
      // speeding up the response when customers select other colors.
      const preloadQueue = (otherColorIds || []).map((colorId = '') => products.$details.bind(null, colorId, {}, { headers: { Preload: 'true' } }))

      for (let i = 0; i < preloadQueue.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        preloadQueue[i]()
      }
    })
  }

  whenever(() => product.value?.id, async () => {
    if (inventoryPending.value)
      await until(inventoryPending).toBe(false)

    pushProductDetailViewEvent(product.value)
  }, { immediate: true })

  // Push updated event with inventory data once it becomes available
  watch(inventory, () => {
    if (inventory.value && product.value) {
      injectFitFinder(product as Ref<Product>, {
        addToCart,
        selectSize,
        cartItems,
        attributeSelection,
        enableColorAsAttribute,
        inventory,
        getAddToCartBtn: () => window['pdp-add-to-cart'],
        getSizeChartLink: () => window['pdp-size-chart']
      })
    }
  }, { immediate: true })

  preselectAttributes()

  useCamera.value = isMobile()

  if (route.query.VTO) tryItOn()
})

onBeforeUnmount(() => {
  resetInteractionOrigin()
  seoMetaHead.value?.dispose()
  header.transparent = false
})

onDeactivated(() => {
  resetInteractionOrigin()
  seoMetaHead.value?.dispose()
})
</script>
